import React from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { PiDownloadSimpleLight, PiTrashLight, PiPaperclipLight } from 'react-icons/pi';
import Badge from '../TailwindComponents/Badge';
import CheckBox from '../TailwindComponents/CheckBox';
import Skeleton from '../TailwindComponents/Skeleton';
import Table from '../TailwindComponents/Table';
import { formatRelativeTime, formatAbsoluteTime } from '../feed/feedTime';
import FileIcon, { fileTypeLabel } from './fileIcon';
import { Attachment, formatFileSize } from './attachmentModel';

/**
 * THE attachment list.
 *
 * The same five files rendered four different ways: a resizable `Table` with
 * an absolute `MMM DD, YYYY HH:mm` stamp on the task tab, a bare row of
 * paperclip links in the matter task panel, another `Table` on contacts with
 * a different column order, and a single hard-coded card on review. Dates
 * were English-only in two of them.
 *
 * One row shape, one date rule (relative label, absolute on `title`, through
 * `feedTime` like every other chronology in the app), one empty state.
 */

export interface AttachmentListProps {
  attachments: Attachment[];
  loading?: boolean;
  /**
   * `canvas` - the list IS the surface (a tab body): roomier rows.
   * `pane` - nested inside a card or surface pane: tighter, quieter rows.
   */
  density?: 'canvas' | 'pane';
  /**
   * `stacked` - name over a `size · uploader · when` metadata line.
   *   Right in a narrow pane, where there is no room for columns.
   * `table` - the shared `Table` (Name, Uploaded by, Uploaded, Size, row
   *   menu): sortable, resizable, scrolls sideways with the name pinned. THE
   *   shape for a tab body, so org/task/contact documents read like Files.
   *   `columns` is kept as an alias for it.
   *
   * Default: `table` at `density="canvas"`, `stacked` at `density="pane"`.
   * A pane is never wide enough for columns, whatever the caller asked for,
   * and a host with bespoke `rowActions` keeps the stacked rows that can
   * carry them.
   */
  layout?: 'stacked' | 'columns' | 'table';
  /** Row press. Without it the name is plain text rather than a control. */
  onOpen?: (attachment: Attachment) => void;
  onDownload?: (attachment: Attachment) => void;
  /**
   * Extra per-row controls, rendered between Download and Remove.
   *
   * For actions only one host has - a playbook source document can be swapped
   * for a newer revision, an ordinary attachment cannot - so the row keeps one
   * shape instead of growing a prop per caller. Return `null` to give a
   * particular row nothing.
   */
  rowActions?: (attachment: Attachment) => React.ReactNode;
  /** Omit to hide the remove control entirely. */
  onRemove?: (attachment: Attachment) => void;
  /** Gate removal per row (or wholesale). Default: allowed when `onRemove` is set. */
  canRemove?: boolean | ((attachment: Attachment) => boolean);
  /** Disables the row controls while a mutation is in flight. */
  busy?: boolean;
  /**
   * Opt-in multi-select. Pass both to get a checkbox per row; the host owns
   * the ids and whatever bulk action it hangs off them.
   */
  selectedIds?: string[];
  onToggleSelect?: (attachment: Attachment) => void;
  emptyTitle?: string;
  emptyHint?: string;
  emptyIcon?: React.ReactNode;
  /** Rows skeleton count while `loading`. Default 4. */
  skeletonRows?: number;
  className?: string;
  'data-tour-id'?: string;
}

const AttachmentList: React.FC<AttachmentListProps> = ({
  attachments,
  loading = false,
  density = 'canvas',
  layout,
  onOpen,
  onDownload,
  rowActions,
  onRemove,
  canRemove,
  busy = false,
  selectedIds,
  onToggleSelect,
  emptyTitle,
  emptyHint,
  emptyIcon,
  skeletonRows = 4,
  className,
  'data-tour-id': dataTourId,
}) => {
  const { t } = useTranslation();
  const compact = density === 'pane';
  const resolvedLayout = layout ?? (compact ? 'stacked' : 'table');
  const asTable = !compact && !rowActions && (resolvedLayout === 'table' || resolvedLayout === 'columns');

  if (asTable && (loading || attachments.length)) {
    return (
      <AttachmentTable
        attachments={attachments}
        loading={loading}
        onOpen={onOpen}
        onDownload={onDownload}
        onRemove={onRemove}
        canRemove={canRemove}
        busy={busy}
        selectedIds={selectedIds}
        onToggleSelect={onToggleSelect}
        skeletonRows={skeletonRows}
        className={className}
        dataTourId={dataTourId}
      />
    );
  }

  if (loading) {
    return (
      <div className={clsx('flex-1 min-h-0 overflow-y-auto', className)} data-tour-id={dataTourId}>
        <Skeleton variant="list" rows={skeletonRows} />
      </div>
    );
  }

  if (!attachments.length) {
    return (
      <div
        className={clsx('flex-1 min-h-0 flex items-center justify-center', compact ? 'py-6' : 'py-12', className)}
        data-tour-id={dataTourId}
      >
        <div className="text-center px-4">
          <div
            className={clsx(
              'mx-auto rounded-full bg-surface-sunken flex items-center justify-center',
              compact ? 'h-9 w-9 mb-2' : 'h-12 w-12 mb-3'
            )}
          >
            {emptyIcon || <PiPaperclipLight className={compact ? 'h-4 w-4 text-fg-muted' : 'h-6 w-6 text-fg-muted'} />}
          </div>
          <p className={clsx('text-fg-strong', compact ? 'text-xs' : 'text-sm font-medium')}>
            {emptyTitle || t('No attachments')}
          </p>
          {!compact && (
            <p className="text-xs text-fg-muted mt-1">
              {emptyHint || t('Files added here appear for everyone with access.')}
            </p>
          )}
        </div>
      </div>
    );
  }

  const removable = (attachment: Attachment) => {
    if (!onRemove) return false;
    if (typeof canRemove === 'function') return canRemove(attachment);
    return canRemove !== false;
  };

  const rows = (
    <ul
      className={clsx(
        'flex-1 min-h-0 overflow-y-auto custom-scrollbar-main',
        compact ? 'space-y-1.5' : 'space-y-2',
        className
      )}
      data-tour-id={dataTourId}
    >
      {attachments.map((attachment) => {
        const size = formatFileSize(attachment.size);
        const when = formatRelativeTime(attachment.uploadedAt, t);
        const whenTitle = formatAbsoluteTime(attachment.uploadedAt);
        const name = attachment.name || t('Untitled file');
        const selected = selectedIds?.includes(attachment.id) ?? false;

        return (
          <li
            key={attachment.id}
            className={clsx(
              'flex items-center gap-3 rounded-md border bg-surface-sunken/70',
              'transition-colors duration-200 ease-slide hover:border-champagne/40',
              selected ? 'border-champagne/60' : 'border-hairline-soft',
              compact ? 'px-2.5 py-2' : 'px-3 py-2.5'
            )}
          >
            {onToggleSelect && (
              <CheckBox
                label={t('Select {{name}}', { name })}
                hideLabel
                translate={false}
                checked={selected}
                onChange={() => onToggleSelect(attachment)}
                className="flex-shrink-0"
              />
            )}
            <FileIcon
              name={attachment.name}
              mime={attachment.mime}
              className={compact ? 'h-4 w-4 flex-shrink-0' : 'h-5 w-5 flex-shrink-0'}
            />

            <div className="flex-1 min-w-0">
              {onOpen ? (
                <button
                  type="button"
                  onClick={() => onOpen(attachment)}
                  title={name}
                  className={clsx(
                    'block w-full text-start truncate text-fg-body hover:text-champagne',
                    'transition-colors duration-200 ease-slide',
                    compact ? 'text-xs' : 'text-sm'
                  )}
                >
                  {name}
                </button>
              ) : (
                <p className={clsx('truncate text-fg-body', compact ? 'text-xs' : 'text-sm')} title={name}>
                  {name}
                </p>
              )}
              <div
                className={clsx(
                  'flex items-center flex-wrap gap-x-2 text-fg-muted',
                  compact ? 'text-[11px] mt-0.5' : 'text-xs mt-1'
                )}
              >
                {size && <span>{size}</span>}
                {attachment.uploadedBy && (
                  <>
                    {size && <span aria-hidden="true">·</span>}
                    <span className="truncate">{attachment.uploadedBy}</span>
                  </>
                )}
                {when && (
                  <>
                    {(size || attachment.uploadedBy) && <span aria-hidden="true">·</span>}
                    <time dateTime={attachment.uploadedAt} title={whenTitle}>
                      {when}
                    </time>
                  </>
                )}
              </div>
            </div>

            {!compact && (
              <Badge color="gray" className="text-[10px] flex-shrink-0">
                {fileTypeLabel(attachment.name, attachment.mime)}
              </Badge>
            )}

            {onDownload && (
              <button
                type="button"
                aria-label={t('Download')}
                title={t('Download')}
                disabled={busy}
                onClick={() => onDownload(attachment)}
                className="p-1.5 rounded-md text-fg-muted hover:text-champagne transition-colors duration-200 ease-slide disabled:opacity-50 flex-shrink-0"
              >
                <PiDownloadSimpleLight className={compact ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
              </button>
            )}

            {rowActions?.(attachment)}

            {removable(attachment) && (
              <button
                type="button"
                aria-label={t('Remove')}
                title={t('Remove')}
                disabled={busy}
                onClick={() => onRemove?.(attachment)}
                className="p-1.5 rounded-md text-fg-muted hover:text-danger-600 transition-colors duration-200 ease-slide disabled:opacity-50 flex-shrink-0"
              >
                <PiTrashLight className={compact ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );

  return rows;
};

const COLUMNS = ['Name', 'Uploaded by', 'Uploaded', 'Size'];

/** The `table` layout: the shared Table, so documents read like Files. */
const AttachmentTable: React.FC<{
  attachments: Attachment[];
  loading: boolean;
  onOpen?: (attachment: Attachment) => void;
  onDownload?: (attachment: Attachment) => void;
  onRemove?: (attachment: Attachment) => void;
  canRemove?: boolean | ((attachment: Attachment) => boolean);
  busy: boolean;
  selectedIds?: string[];
  onToggleSelect?: (attachment: Attachment) => void;
  skeletonRows: number;
  className?: string;
  dataTourId?: string;
}> = ({
  attachments, loading, onOpen, onDownload, onRemove, canRemove, busy,
  selectedIds, onToggleSelect, skeletonRows, className, dataTourId,
}) => {
  const { t } = useTranslation();
  const byId = React.useMemo(() => new Map(attachments.map((a) => [a.id, a])), [attachments]);
  const removable = (a: Attachment) =>
    !!onRemove && (typeof canRemove === 'function' ? canRemove(a) : canRemove !== false);

  const actions = [
    ...(onDownload ? [{
      label: t('Download'),
      icon: <PiDownloadSimpleLight className="h-4 w-4" />,
      onClick: (row: any) => onDownload(byId.get(row.id)!),
      disabled: () => busy,
    }] : []),
    ...(onRemove ? [{
      label: t('Remove'),
      icon: <PiTrashLight className="h-4 w-4" />,
      onClick: (row: any) => onRemove(byId.get(row.id)!),
      disabled: () => busy,
      hidden: (row: any) => !removable(byId.get(row.id)!),
      isDestructive: true,
    }] : []),
  ];

  const data = attachments.map((a) => ({
    id: a.id,
    Name: a.name || t('Untitled file'),
    'Uploaded by': a.uploadedBy || '',
    Uploaded: a.uploadedAt || '',
    Size: a.size ?? 0,
  }));

  const selectable = !!onToggleSelect;
  const selected = selectedIds ?? [];

  return (
    <div className={clsx('flex-1 min-h-0 flex flex-col', className)} data-tour-id={dataTourId}>
      <Table
        columns={actions.length ? [...COLUMNS, 'Actions'] : COLUMNS}
        data={data}
        tableId="attachments"
        isLoading={loading}
        skeletonRows={skeletonRows}
        clientSort
        sortableColumns={COLUMNS}
        sortAccessors={{
          Uploaded: (row) => (row.Uploaded ? new Date(row.Uploaded).getTime() : 0),
          Size: (row) => row.Size,
        }}
        defaultColumnWidths={{ Name: 320, 'Uploaded by': 180, Uploaded: 170, Size: 100 }}
        enableSelection={selectable}
        selectedIds={selected}
        onSelect={(id) => onToggleSelect?.(byId.get(id)!)}
        onSelectAll={() => {
          const allOn = attachments.every((a) => selected.includes(a.id));
          attachments
            .filter((a) => allOn || !selected.includes(a.id))
            .forEach((a) => onToggleSelect?.(a));
        }}
        onRowClick={onOpen ? (row) => onOpen(byId.get(row.id)!) : undefined}
        actions={actions.length ? actions : undefined}
        renderCell={(col, value, row) => {
          const a = byId.get(row.id);
          if (!a) return value;
          if (col === 'Name') {
            return (
              <span className="flex items-center gap-2 min-w-0">
                <FileIcon name={a.name} mime={a.mime} className="h-5 w-5 flex-shrink-0" />
                <span className="truncate" title={value}>{value}</span>
              </span>
            );
          }
          if (col === 'Uploaded') {
            return a.uploadedAt ? (
              <time dateTime={a.uploadedAt} title={formatAbsoluteTime(a.uploadedAt)}>
                {formatRelativeTime(a.uploadedAt, t)}
              </time>
            ) : '—';
          }
          if (col === 'Size') {
            return <span className="tabular-nums text-fg-muted">{formatFileSize(a.size) || '—'}</span>;
          }
          if (col === 'Uploaded by') return value || '—';
          return value;
        }}
      />
    </div>
  );
};

export default AttachmentList;
